import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Search, Xmark, List, NavArrowLeft, Calendar } from 'iconoir-react';
import { C, St, FONT_BODY } from '../theme';
import { buildColorMap, rankSchede, sessionFitsScheda } from '../utils';
import { calcVolume, progressionComment, detectStalls, buildExerciseInsights, checkDeloadNeeded, detectOverreaching } from '../science';
import { _globalSettings } from '../constants';

// ─── SEARCH BAR ───────────────────────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Cerca…', style }) {
  const ref = useRef(null);
  const onFocus = () => { if (ref.current) ref.current.style.boxShadow = `0 0 0 3px ${C.blue}25`; };
  const onBlur  = () => { if (ref.current) ref.current.style.boxShadow = 'none'; };
  return (
    <div ref={ref} style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#fff', borderRadius: 10,
      padding: '0 14px', height: 44,
      boxShadow: 'none', border: '1px solid #EAEAEA',
      transition: 'box-shadow 0.15s',
      ...style,
    }}>
      <Search width={15} height={15} color={C.muted} style={{ flexShrink: 0 }}/>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontFamily: FONT_BODY, fontSize: 16, color: C.text, minWidth: 0,
        }}
      />
      {value.length > 0 && (
        <button onClick={() => onChange('')} style={{
          background: 'rgba(120,120,128,0.2)', border: 'none', borderRadius: 999,
          width: 20, height: 20, cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Xmark width={10} height={10} color={C.muted}/>
        </button>
      )}
    </div>
  );
}

// ─── COMMENT CARD ─────────────────────────────────────────────────────────────
export function CommentCard({c}) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  if (!c) return null;
  const items=Array.isArray(c)?c:[c];
  const renderText=(text,col)=>{
    const lines=text.split("\n");
    if (!lines.some(l=>l.includes("|"))) return <div style={{fontSize:12,color:C.muted,lineHeight:1.7,whiteSpace:"pre-line"}}>{text}</div>;
    return (<div style={{marginTop:6}}>{lines.map((line,i)=>{
      if (line.includes("|")){
        const parts=line.split("|").map(p=>p.trim());
        const isHdr=!line.match(/S\d/);
        return (<div key={i} style={{display:"grid",gridTemplateColumns:"72px 1fr 1fr",gap:4,padding:"5px 8px",borderRadius:7,background:isHdr?col+"18":i%2===0?"transparent":col+"08",marginBottom:2}}>
          <div style={{...St.mono,fontSize:11,color:C.subtle,minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>{parts[0]}</div>
          <div style={{...St.mono,fontSize:12,color:isHdr?C.subtle:C.muted,textAlign:"center",minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>{parts[1]}</div>
          <div style={{...St.mono,fontSize:12,color:isHdr?C.subtle:col,textAlign:"center",fontWeight:600,minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>{parts[2]}</div>
        </div>);
      }
      if (!line.trim()) return <div key={i} style={{height:4}}/>;
      return <div key={i} style={{fontSize:12,color:C.muted,lineHeight:1.7,marginTop:2}}>{line}</div>;
    })}</div>);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:10}}>
      {items.map((item,i)=>{
        const mainText = item.copy || item.body;
        const detailText = item.detail || (item.copy ? item.body : null);
        const hasDetail = !!detailText;
        const isOpen = expandedIdx === i;
        return (
          <div key={i} style={{background:item.bg,borderRadius:14,padding:"14px 16px",overflow:"hidden"}}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{width:6,flexShrink:0,marginTop:3,alignSelf:"stretch",borderRadius:3,background:item.color}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:4}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.text,flex:1}}>{item.title}</div>
                  {hasDetail&&(
                    <button onClick={()=>setExpandedIdx(isOpen?null:i)}
                      style={{flexShrink:0,width:22,height:22,borderRadius:11,border:"none",cursor:"pointer",
                        background:`${item.color}20`,color:item.color,fontSize:11,fontWeight:700,
                        display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FONT_BODY,lineHeight:1}}>
                      ⓘ
                    </button>
                  )}
                </div>
                {renderText(mainText, item.color)}
                {isOpen&&detailText&&(
                  <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${item.color}25`,
                    fontSize:11,color:C.subtle,lineHeight:1.65,whiteSpace:"pre-line",fontStyle:"italic",fontFamily:FONT_BODY}}>
                    {detailText}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DAILY COACH MESSAGE ─────────────────────────────────────────────────────

const SF_MONO = FONT_BODY;

function Bdg({ text, color, mono = false }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '1px 10px 2px', borderRadius: 99,
      background: `${color}18`, color,
      fontWeight: 600, fontSize: '0.88em',
      fontFamily: mono ? SF_MONO : FONT_BODY,
      verticalAlign: 'baseline', position: 'relative', top: '-1px',
      marginInline: 2, whiteSpace: 'nowrap', lineHeight: 1.65,
    }}>{text}</span>
  );
}

export function DailyCoachMessage({ db, userName, nextWidget }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [checkedItems, setCheckedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gp_coach_checked') || '{}'); }
    catch { return {}; }
  });
  const [expandedInfo, setExpandedInfo] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('gp_coach_checked', JSON.stringify(checkedItems));
  }, [checkedItems]);

  // Auto-scroll — solo quando arriva un nuovo messaggio reale (non il widget iniziale)
  React.useEffect(() => {
    if (chatMessages.length > 1) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Apre chat: inserisce prima il widget come bubble, poi chiama Gemini
  React.useEffect(() => {
    if (!chatOpen) return;
    if (chatMessages.length > 0) return;
    setChatMessages([{ role: 'widget' }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen]);

  // ── tutti gli hook prima di qualsiasi early return ────────────────────────
  const sessions  = useMemo(() => [...db.sessions].sort((a, b) => b.date.localeCompare(a.date)), [db.sessions]);
  const schede    = useMemo(() => db.schede || [], [db.schede]);
  const weightLog = useMemo(() => db.weightLog || [], [db.weightLog]);
  const colorMap  = useMemo(() => buildColorMap(schede, sessions), [schede, sessions]);
  const ranked    = useMemo(() => schede.length ? rankSchede(schede.filter(s=>s.active!==false), sessions) : [], [schede, sessions]);

  const nextScheda  = ranked[0] || null;
  const nextWorkout = nextScheda?.name || null;
  const nextColor   = colorMap[nextWorkout] || C.blue;
  const isInactive  = nextScheda && (nextScheda.daysAgo > 14 || nextScheda.daysAgo === 999);
  const isNever     = nextScheda?.daysAgo === 999;

  const lastNextSess = useMemo(() =>
    nextWorkout ? sessions.find(s => sessionFitsScheda(s.workoutName, nextWorkout)) : null
  , [sessions, nextWorkout]);

  const exInsights = useMemo(() => {
    if (!lastNextSess) return [];
    return lastNextSess.exercises.map(ex => {
      const prevSess = sessions.find(s => s.date < lastNextSess.date && s.exercises.some(e => e.name === ex.name));
      const prevEx   = prevSess?.exercises.find(e => e.name === ex.name);
      const _intens = _globalSettings.intensitaAttiva === false ? 'off' : (_globalSettings.intensita || 'entrambi');
      const insights = buildExerciseInsights(ex.sets, ex.type || 'compound', prevEx?.sets || null, ex.name, sessions, 'auto', _globalSettings.repRange, { showRpe: _intens === 'rpe' || _intens === 'entrambi' });
      const primary  = insights?.primaryInsight
        || (prevEx ? progressionComment(ex.sets, ex.type || 'compound', prevEx.sets, _globalSettings.repRange) : null);
      return { name: ex.name, comment: primary || null, sets: ex.sets || [] };
    });
  }, [lastNextSess, sessions]);

  const stalls  = useMemo(() => detectStalls(sessions), [sessions]);
  const deload  = useMemo(() => checkDeloadNeeded(db.sessions), [db.sessions]);
  const overreachingExs = useMemo(() => {
    if (!lastNextSess) return [];
    return lastNextSess.exercises
      .map(ex => detectOverreaching(db.sessions, ex.name))
      .filter(Boolean);
  }, [lastNextSess, db.sessions]);
  const recomp  = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 14);
    const recent = [...weightLog].filter(w => new Date(w.date) >= cutoff).sort((a, b) => a.date.localeCompare(b.date));
    if (recent.length < 2) return null;
    const wDelta = recent[recent.length - 1].weight - recent[0].weight;
    const cutoff2 = new Date(); cutoff2.setDate(cutoff2.getDate() - 28);
    const vol = (arr) => arr.length ? arr.reduce((a, s) => a + s.exercises.reduce((b, e) => b + calcVolume(e.sets), 0), 0) / arr.length : 0;
    const volUp = vol(sessions.filter(s => new Date(s.date) >= cutoff)) > vol(sessions.filter(s => new Date(s.date) >= cutoff2 && new Date(s.date) < cutoff)) * 1.02;
    if (wDelta <= 0.5 && volUp) return { label: 'Ricomposizione', color: C.green };
    if (wDelta > 1.5) return { label: 'Attenzione', color: C.red };
    return { label: 'Monitorare', color: C.amber };
  }, [weightLog, sessions]);

  // ── valori derivati (non hook) ─────────────────────────────────────────────
  const hasAnyExercises = schede.some(s => s.exercises?.length > 0);
  const stallName = lastNextSess
    ? stalls.find(name => lastNextSess.exercises.some(e => e.name === name)) || null
    : null;
  const daysSinceNext = lastNextSess
    ? Math.floor((Date.now() - new Date(lastNextSess.date).getTime()) / 86400000)
    : null;
  const daysColor = daysSinceNext === null ? C.subtle
    : daysSinceNext === 0 ? C.subtle
    : daysSinceNext === 1 ? C.amber
    : daysSinceNext >= 2 && daysSinceNext <= 5 ? C.green
    : daysSinceNext > 10 ? C.red : C.subtle;
  const daysLabel = daysSinceNext === 0 ? 'oggi'
    : daysSinceNext === 1 ? '1 giorno fa'
    : daysSinceNext !== null ? `${daysSinceNext} giorni fa` : null;

  // ── early returns dopo tutti gli hook ─────────────────────────────────────
  if (!schede.length) return null;

  const NAR = { fontFamily: FONT_BODY, fontSize: 17, lineHeight: 1.75, color: C.text, fontWeight: 400, overflowX: 'hidden', wordBreak: 'break-word' };

  if (!hasAnyExercises) {
    return (
      <div style={{ ...NAR, fontSize: 17, color: C.subtle, padding: '4px 0 20px' }}>
        {'Aggiungi esercizi a '}
        <Bdg text={nextWorkout || 'una scheda'} color={C.amber} />
        {' per sbloccare il briefing giornaliero.'}
      </div>
    );
  }

  if (!lastNextSess) {
    return (
      <div style={{ ...NAR, padding: '4px 0 20px' }}>
        {'Prima sessione di '}
        <Bdg text={nextWorkout} color={nextColor} />
        {'. Carica la sessione per iniziare a tracciare i progressi.'}
      </div>
    );
  }

  // ── Build full briefing paragraph ─────────────────────────────────────────
  const fmtSets = (sets) => {
    const working = sets.filter(s => parseFloat(s.weight) > 0 && parseInt(s.reps) > 0);
    if (!working.length) return null;
    const groups = [];
    working.forEach(s => {
      const w = parseFloat(s.weight), r = parseInt(s.reps);
      const last = groups[groups.length - 1];
      if (last && last.w === w && last.r === r) { last.n++; }
      else groups.push({ w, r, n: 1 });
    });
    return groups.map(g => g.n > 1 ? `${g.n}x${g.r} a ${g.w}kg` : `${g.r} a ${g.w}kg`).join(', ');
  };

  // ── Regole silenziose (non mostrate nelle azioni) ─────────────────────────
  const SILENT_RULE_IDS = new Set(['TR-05']);
  // Copy diretta per azioni singole — trasforma il testo in istruzione operativa
  const ACTION_COPY_FN = {
    'TR-01': (ex) => `Riduci il carico su ${ex.name} e fai un deload leggero prima di riprendere peso.`,
    'TR-02': (ex) => `Abbassa il peso su ${ex.name}: punta a 6-10 reps controllate per serie.`,
    'TR-04': (ex) => `Aggiungi carico su ${ex.name} alla prossima sessione: il tuo corpo e' pronto.`,
  };
  const RULE_AGGREGATE_COPY = {
    'TR-01': (n, names) => `Fai un deload su ${names.join(', ')}: crollo tra serie rilevato su ${n} esercizi.`,
    'TR-02': (n, names) => `Abbassa il carico su ${names.join(', ')}: le reps di partenza sono troppo basse.`,
    'TR-04': (n, names) => `Aggiungi peso alla prossima sessione su ${names.join(', ')}: le reps sono stabili da piu' sedute.`,
  };
  const MOTIVATIONAL = [
    'La consistenza costruisce risultati che il talento da solo non può.',
    'Ogni sessione registrata è un mattone. Stai costruendo qualcosa di solido.',
    'Il progresso non è sempre visibile, ma i dati non mentono.',
    'La migliore versione di te si allena anche quando non ne hai voglia.',
    'Chi registra, migliora. Chi migliora, continua.',
  ];

  // Pill base — uniforme per giorni e biometriche
  const PILL_BASE = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    borderRadius: 20, padding: '5px 8px',
    fontSize: 17, fontWeight: 600, lineHeight: 1,
    verticalAlign: 'middle',
  };

  const sentences = [];

  // S1 — intro + badge scheda + pill giorni
  const introText = (() => {
    if (isInactive) return `Sono passati ${daysSinceNext} giorni dall'ultima`;
    if (daysSinceNext >= 7) return "E' passata una settimana dall'ultima";
    if (daysSinceNext >= 3) return "Buon recupero dall'ultima";
    if (daysSinceNext === 2) return "Due giorni di recupero dall'ultima";
    if (daysSinceNext === 1) return "Ancora fresco dall'ultima";
    if (daysSinceNext === 0) return "Hai gia' allenato";
    return "Oggi tocca";
  })();
  const s1Parts = [];
  s1Parts.push(<span key="intro">{introText}{' '}</span>);
  s1Parts.push(<Bdg key="sch" text={nextWorkout} color={nextColor} />);
  if (isInactive) s1Parts.push(<Bdg key="inact" text={isNever ? 'Mai allenata' : 'Inattiva'} color={C.amber} />);
  // Pill giorni — stesso stile PILL_BASE
  if (daysLabel) {
    s1Parts.push(
      <span key="daypill" style={{ ...PILL_BASE, background: 'rgba(120,120,128,0.12)', color: daysColor, marginLeft: 4 }}>
        <Calendar width={14} height={14} color={daysColor} strokeWidth={2} />
        {daysLabel}
      </span>
    );
  }
  // Esercizi silenziosi inline — max 2, quelli esclusi da prossime azioni
  const silentInsights = exInsights.filter(e => {
    if (!e.comment?.copy) return false;
    const ruleId = e.comment.ruleId;
    if (ruleId && SILENT_RULE_IDS.has(ruleId)) return true;
    if (ruleId === 'TR-03' && e.comment.status === 'optimal') return true;
    return false;
  }).slice(0, 2);

  if (silentInsights.length > 0) {
    s1Parts.push(<span key="s1dot">{' '}</span>);
    silentInsights.forEach((ex, i) => {
      s1Parts.push(
        <span key={`si${i}`}>
          {i === 0 ? 'Su ' : 'e '}
          <Bdg text={ex.name} color={ex.comment.color} />
          {`: ${ex.comment.copy.charAt(0).toLowerCase()}${ex.comment.copy.slice(1).replace(/\.$/, '')}`}
          {i === silentInsights.length - 1 ? '.' : '; '}
        </span>
      );
    });
  } else {
    s1Parts.push(<span key="s1dot">{'.'}</span>);
  }

  sentences.push(<span key="s1">{s1Parts}</span>);


  sentences.push(
    <span key="cta" style={{ color: C.subtle }}>
      {' Per il tuo prossimo allenamento, hai azioni consigliate.'}
    </span>
  );

  const HDG = { fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.01em' };

  // S_actions — prossime azioni con aggregazione per ruleId e regole silenziose
  const actionsSentences = [];

  // Filtra: solo insight con copy, non silenziosi, non TR-03 ottimale
  const actionableInsights = exInsights.filter(e => {
    if (!e.comment?.copy) return false;
    const ruleId = e.comment.ruleId;
    if (ruleId && SILENT_RULE_IDS.has(ruleId)) return false;
    if (ruleId === 'TR-03' && e.comment.status === 'optimal') return false;
    return true;
  });

  const RULE_DISPLAY = {
    'TR-01': { label: 'Fai deload',         svgKey: 'deload'    },
    'TR-02': { label: 'Abbassa carico',     svgKey: 'lowerLoad' },
    'TR-03': { label: 'Ottimizza back-off', svgKey: 'raise'     },
    'TR-04': { label: 'Aggiungi carico',    svgKey: 'raise'     },
    'TR-05': { label: 'Consolida',          svgKey: 'hold'      },
    'STALL':    { label: 'Stallo rilevato',    svgKey: 'stall'     },
    'RECOMP':   { label: 'Monitora',           svgKey: 'recomp'    },
    'DELOAD':   { label: 'Deload preventivo',  svgKey: 'deload'    },
    'OVERREACH':{ label: 'Overreaching',       svgKey: 'deload'    },
  };

  // Costruisce lista checklist items
  const checklistItems = [];

  if (actionableInsights.length > 0) {
    const byRuleId = {};
    const noRuleId = [];
    actionableInsights.forEach(ex => {
      const ruleId = ex.comment.ruleId;
      if (ruleId) {
        if (!byRuleId[ruleId]) byRuleId[ruleId] = [];
        byRuleId[ruleId].push(ex);
      } else {
        noRuleId.push(ex);
      }
    });

    Object.entries(byRuleId).forEach(([ruleId, exs], ri) => {
      const n = exs.length;
      const names = exs.map(e => e.name);
      const aggFn = RULE_AGGREGATE_COPY[ruleId];
      if (n >= 2 && aggFn) {
        checklistItems.push({ key: `agg${ri}`, color: exs[0].comment.color, bg: exs[0].comment.bg, border: exs[0].comment.border, ruleId, actionCopy: aggFn(n, names) });
      } else {
        exs.forEach((ex, i) => {
          const directFn = ACTION_COPY_FN[ruleId];
          const copy = directFn ? directFn(ex) : ex.comment.copy;
          checklistItems.push({ key: `rule${ri}_${i}`, color: ex.comment.color, bg: ex.comment.bg, border: ex.comment.border, ruleId: ex.comment.ruleId, exName: ex.name, actionCopy: copy, prevSets: fmtSets(ex.sets) || null });
        });
      }
    });

    noRuleId.forEach((ex, i) => {
      checklistItems.push({ key: `ind${i}`, color: ex.comment.color, bg: ex.comment.bg, border: ex.comment.border, ruleId: ex.comment.ruleId, exName: ex.name, actionCopy: ex.comment.copy, prevSets: fmtSets(ex.sets) || null });
    });
  }

  // Stall mention
  const allActionNames = actionableInsights.map(e => e.name);
  if (stallName && !allActionNames.includes(stallName)) {
    checklistItems.push({ key: 'stall', color: C.amber, bg: C.amberL, border: C.amberB, ruleId: 'STALL', exName: stallName, actionCopy: 'Stallo rilevato.' });
  }

  // Recomp mention
  if (recomp) {
    const recompBg = recomp.color === C.green ? C.greenL : recomp.color === C.red ? C.redL : C.amberL;
    const recompBorder = recomp.color === C.green ? C.greenB : recomp.color === C.red ? C.redB : C.amberB;
    checklistItems.push({ key: 'rec', color: recomp.color, bg: recompBg, border: recompBorder, ruleId: 'RECOMP', actionCopy: 'Monitora.' });
  }

  // Deload preventivo
  if (_globalSettings.deloadAuto !== false && deload.needed) {
    checklistItems.push({ key: 'deload', color: C.cyan, bg: C.cyanL, border: C.cyanB, ruleId: 'DELOAD', actionCopy: `Questa settimana riduci il volume del 40-50%: hai accumulato ${deload.weeksSinceDeload} settimane di carico progressivo.` });
  }

  // Overreaching cronico (solo se 2+ esercizi)
  if (overreachingExs.length >= 2) {
    checklistItems.push({ key: 'overreach', color: C.amber, bg: C.amberL, border: C.amberB, ruleId: 'OVERREACH', actionCopy: `RPE in aumento su ${overreachingExs.length} esercizi a carico invariato. Valuta un giorno di recupero extra.` });
  }

  if (checklistItems.length > 0) {
    actionsSentences.push(
      <div key="sep-act" style={{ ...HDG, display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, marginBottom: 10 }}>
        <List width={18} height={18} color={C.text} strokeWidth={2} />
        {'Prossime azioni:'}
      </div>
    );
    actionsSentences.push(
      <div key="checklist" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {checklistItems.map(item => {
          const checked = !!checkedItems[item.key];
          const display = RULE_DISPLAY[item.ruleId] || RULE_DISPLAY['TR-05'];
          const infoOpen = expandedInfo === item.key;
          return (
            <div key={item.key} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                {/* Checkbox */}
                <button
                  onClick={() => setCheckedItems(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 11,
                    border: checked ? 'none' : '2px solid rgba(120,120,128,0.4)',
                    background: checked ? C.green : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}>
                    {checked && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </button>
                {/* Pill */}
                <Bdg text={display.label} color={checked ? C.subtle : item.color} />
                {/* Nome + sets */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {item.exName && (
                    <span style={{ fontFamily: FONT_BODY, fontSize: 14, color: checked ? C.subtle : C.text, textDecoration: checked ? 'line-through' : 'none', transition: 'all 0.15s ease' }}>
                      {item.exName}
                    </span>
                  )}
                  {item.prevSets && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <circle cx="6" cy="6" r="5" stroke={C.subtle} strokeWidth="1.4"/>
                        <path d="M6 3.5v2.7l1.8 1.1" stroke={C.subtle} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span style={{ fontFamily: FONT_BODY, fontSize: 11, color: C.subtle, fontWeight: 500 }}>Scorsa sessione:</span>
                      <span style={{ fontFamily: FONT_BODY, fontSize: 11, color: C.subtle }}>{item.prevSets}</span>
                    </div>
                  )}
                </div>
                {/* Info icon */}
                {item.actionCopy && (
                  <button
                    onClick={() => setExpandedInfo(infoOpen ? null : item.key)}
                    style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', flexShrink: 0, lineHeight: 0 }}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="9" stroke={infoOpen ? item.color : C.subtle} strokeWidth="1.4"/>
                      <path d="M10 9v5" stroke={infoOpen ? item.color : C.subtle} strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="10" cy="6.5" r="0.8" fill={infoOpen ? item.color : C.subtle}/>
                    </svg>
                  </button>
                )}
              </div>
              {/* Accordion */}
              {infoOpen && item.actionCopy && (
                <div style={{ marginLeft: 34, marginBottom: 4, paddingBottom: 6 }}>
                  <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.10)', marginBottom: 8 }} />
                  <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: C.subtle, lineHeight: 1.65 }}>
                    {item.actionCopy}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const motivText = MOTIVATIONAL[sessions.length % MOTIVATIONAL.length];


  // Contenuto widget completo — usato nella bubble chat
  const widgetContent = (
    <>
      <div style={NAR}>{sentences}</div>
      {nextWidget && <div style={{ marginTop: 16 }}>{nextWidget}</div>}

      <div style={{ marginTop: 14, fontFamily: FONT_BODY, fontSize: 15, color: C.subtle, lineHeight: 1.6, fontStyle: 'italic' }}>
        {userName ? `${motivText} — ${userName}.` : `${motivText}`}
      </div>
    </>
  );

  return (
    <>
      <div style={{ marginBottom: 0 }}>
      </div>

      {/* Chat overlay — portal su document.body per evitare stacking context della dashboard */}
      {chatOpen && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: '#FFFFFF', zIndex: 300,
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* Header — sticky in alto, non scrolla */}
          <div style={{
            flexShrink: 0, maxWidth: 560, width: '100%', margin: '0 auto',
            paddingTop: 'max(56px, calc(env(safe-area-inset-top, 0px) + 14px))',
            paddingRight: 20, paddingBottom: 10, paddingLeft: 20,
            background: '#FFFFFF',
            borderBottom: '0.5px solid rgba(60,60,67,0.08)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
              <button
                onClick={() => setChatOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px 0 0', lineHeight: 0, flexShrink: 0 }}
              >
                <NavArrowLeft width={22} height={22} color={C.text} strokeWidth={2} />
              </button>
              <div style={{ fontFamily: FONT_BODY, fontSize: 28, fontWeight: 700, letterSpacing: -0.5, color: C.text, lineHeight: 1 }}>
                Suggerimenti
              </div>
            </div>
          </div>

          {/* Messaggi — area scrollabile, slide-down + fade */}
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
            maxWidth: 560, width: '100%', margin: '0 auto',
            padding: '8px 20px 32px', display: 'flex', flexDirection: 'column', gap: 10,
            animation: 'slideDownContent 1.2s cubic-bezier(0.32,0.72,0,1) both' }}>
            <style>{`@keyframes slideDownContent { from { opacity: 0; transform: translateY(-18px); } to { opacity: 1; transform: translateY(0); } }`}</style>
              {chatMessages.map((m, i) => {
                if (m.role === 'widget') {
                  return (
                    <div key={i} style={{ marginBottom: 4 }}>
                      {widgetContent}
                    </div>
                  );
                }
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '82%', padding: '10px 14px',
                      background: m.role === 'user' ? C.blue : C.card,
                      color: m.role === 'user' ? '#fff' : C.text,
                      borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55,
                      boxShadow: m.role === 'coach' ? '0 2px 8px rgba(0,0,0,0.07)' : 'none',
                    }}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {false && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ padding: '10px 14px', background: C.card, borderRadius: '16px 16px 16px 4px', fontFamily: FONT_BODY, fontSize: 18, color: C.subtle, letterSpacing: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                    {'···'}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

        </div>
      , document.body)}
    </>
  );
}

